import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { BUSINESS_TYPE_OPTIONS } from '../utils/businessProfiles';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', text: '#E8EDF5', sub: '#C5CDD8',
};

const EMPTY_PRODUCT = { name: '', price: '', quantity: '' };
const EMPTY_CUSTOMER = { name: '', contact: '' };

function ConfettiPiece({ style }) {
  return <div style={{ position: 'absolute', width: 8, height: 8, borderRadius: 2, animation: 'confettiFall 3s ease-in forwards', ...style }} />;
}

const CONFETTI_COLORS = [C.gold, C.teal, '#FFCD3C', C.coral, '#A78BFA', '#38BDF8'];

export default function Onboarding() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState({ products: [], customer: null });
  const [setupError, setSetupError] = useState('');

  // Step 1
  const [businessName, setBusinessName] = useState(user?.businessName || '');
  const [businessType, setBusinessType] = useState('retail');

  // Step 2
  const [products, setProducts] = useState([{ ...EMPTY_PRODUCT }]);

  // Step 3
  const [customer, setCustomer] = useState({ ...EMPTY_CUSTOMER });

  useEffect(() => {
    let mounted = true;

    api.get('/workspaces/current/profile')
      .then(({ data }) => {
        if (!mounted || !data) return;
        setBusinessName(data.name || user?.businessName || '');
        setBusinessType(data.businessType || 'retail');
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, [user?.businessName]);

  const addProduct = () => {
    if (products.length < 3) setProducts((p) => [...p, { ...EMPTY_PRODUCT }]);
  };
  const updateProduct = (i, field, val) =>
    setProducts((p) => p.map((row, idx) => (idx === i ? { ...row, [field]: val } : row)));

  const saveStep1 = async () => {
    if (!businessName.trim() || !businessType) return;
    setSaving(true);
    setSetupError('');

    try {
      const normalizedName = businessName.trim();
      const [profileRes] = await Promise.all([
        api.put('/auth/update-profile', {
          name: user?.name || 'OptiKa User',
          businessName: normalizedName,
        }),
        api.put('/workspaces/current/profile', {
          name: normalizedName,
          businessType,
        }),
      ]);

      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      if (token && refreshToken && profileRes.data?.user) {
        login(token, refreshToken, profileRes.data.user);
      }

      setStep(2);
    } catch (error) {
      setSetupError(error.response?.data?.error || 'Failed to save your business profile.');
    } finally {
      setSaving(false);
    }
  };

  const saveStep2 = async () => {
    const valid = products.filter((p) => p.name.trim() && p.price);
    setSaving(true);
    const created = [];
    for (const p of valid) {
      try {
        const { data } = await api.post('/inventory', {
          name: p.name.trim(),
          price: parseFloat(p.price) || 0,
          cost: parseFloat(p.price) * 0.6 || 0,
          quantity: parseInt(p.quantity) || 0,
        });
        created.push(data);
      } catch { /* skip errors */ }
    }
    setSummary((s) => ({ ...s, products: created }));
    setSaving(false);
    setStep(3);
  };

  const saveStep3 = async () => {
    if (customer.name.trim()) {
      setSaving(true);
      try {
        const payload = { name: customer.name.trim() };
        if (customer.contact.includes('@')) payload.email = customer.contact;
        else if (customer.contact.trim()) payload.phone = customer.contact;
        const { data } = await api.post('/customers', payload);
        setSummary((s) => ({ ...s, customer: data }));
      } catch { /* skip */ }
      setSaving(false);
    }
    setStep(4);
  };

  const finish = () => {
    localStorage.setItem('optika_onboarding_complete', 'true');
    navigate('/dashboard');
  };

  const confetti = Array.from({ length: 30 }, (_, i) => ({
    left: `${Math.random() * 100}%`,
    top: `${-10 - Math.random() * 10}%`,
    background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    animationDelay: `${Math.random() * 1.5}s`,
    animationDuration: `${2 + Math.random() * 2}s`,
    transform: `rotate(${Math.random() * 360}deg)`,
    width: `${6 + Math.random() * 8}px`,
    height: `${6 + Math.random() * 8}px`,
  }));

  const containerStyle = {
    minHeight: '100vh', background: C.ink, display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: 24, position: 'relative', overflow: 'hidden',
    fontFamily: "'Cabinet Grotesk', sans-serif",
  };

  const cardStyle = {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, padding: '40px 40px', maxWidth: 520, width: '100%',
    boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
  };

  const inputStyle = {
    width: '100%', background: 'rgba(255,255,255,0.04)', border: `1.5px solid ${C.border}`, borderRadius: 10,
    padding: '10px 14px', color: C.text, fontSize: '0.9rem', outline: 'none',
    boxSizing: 'border-box', marginTop: 6, fontFamily: 'inherit',
  };

  const primaryBtn = { background: C.gold, color: C.ink, border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
  const skipBtn = { background: 'none', border: `1px solid ${C.border}`, color: C.muted, borderRadius: 10, cursor: 'pointer', fontSize: '0.875rem', fontFamily: 'inherit' };

  return (
    <div style={containerStyle}>
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>

      {step === 4 && confetti.map((c, i) => <ConfettiPiece key={i} style={c} />)}

      {/* Progress dots */}
      <div style={{ position: 'absolute', top: 28, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8 }}>
        {[1, 2, 3, 4].map((n) => (
          <div key={n} style={{ width: n <= step ? 24 : 8, height: 8, borderRadius: 4, background: n <= step ? C.gold : C.border, transition: 'all 0.3s' }} />
        ))}
      </div>

      {/* ── Step 1 ── */}
      {step === 1 && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: C.text, marginBottom: 8 }}>
            Welcome to OptiKa, {user?.name?.split(' ')[0]}! 🎉
          </h2>
          <p style={{ color: C.muted, marginBottom: 28 }}>Let us shape OptiKa around your trade business before you start.</p>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: C.sub }}>Business name</label>
            <input style={inputStyle} value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="e.g. Sunrise General Store" />
          </div>

          <div style={{ marginBottom: 32 }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: C.sub }}>Business model</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
              {BUSINESS_TYPE_OPTIONS.map((option) => (
                <button key={option.id} onClick={() => setBusinessType(option.id)} style={{
                  padding: '12px 14px', borderRadius: 10, border: '1px solid',
                  borderColor: businessType === option.id ? C.gold : C.border,
                  background: businessType === option.id ? 'rgba(240,180,41,0.12)' : 'rgba(255,255,255,0.03)',
                  color: businessType === option.id ? C.gold : C.sub,
                  cursor: 'pointer', fontSize: '0.85rem', fontWeight: businessType === option.id ? 700 : 500, textAlign: 'left',
                  fontFamily: 'inherit',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: '1rem' }}>{option.icon}</span>
                    <span>{option.label}</span>
                  </div>
                  <div style={{
                    fontSize: '0.73rem',
                    lineHeight: 1.45,
                    color: businessType === option.id ? C.sub : C.muted,
                    fontWeight: 500,
                  }}>
                    {option.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {setupError && (
            <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 8, background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.25)', color: C.coral, fontSize: '0.82rem' }}>
              {setupError}
            </div>
          )}

          <button onClick={saveStep1} disabled={!businessName.trim() || !businessType || saving}
            style={{ ...primaryBtn, width: '100%', padding: 12, fontSize: '0.95rem', opacity: (!businessName.trim() || !businessType) ? 0.5 : 1 }}>
            {saving ? 'Saving…' : 'Continue →'}
          </button>
        </div>
      )}

      {/* ── Step 2 ── */}
      {step === 2 && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: C.text, marginBottom: 8 }}>What do you sell?</h2>
          <p style={{ color: C.muted, marginBottom: 24 }}>Add your first products to get started</p>

          {products.map((p, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 2, minWidth: 160 }}>
                  <label style={{ fontSize: '0.78rem', color: C.muted }}>Product name</label>
                  <input style={inputStyle} value={p.name} onChange={(e) => updateProduct(i, 'name', e.target.value)} placeholder="e.g. Ankara Fabric" />
                </div>
                <div style={{ flex: 1, minWidth: 90 }}>
                  <label style={{ fontSize: '0.78rem', color: C.muted }}>Price</label>
                  <input style={inputStyle} type="number" value={p.price} onChange={(e) => updateProduct(i, 'price', e.target.value)} placeholder="0.00" min="0" step="0.01" />
                </div>
                <div style={{ flex: 1, minWidth: 90 }}>
                  <label style={{ fontSize: '0.78rem', color: C.muted }}>Stock qty</label>
                  <input style={inputStyle} type="number" value={p.quantity} onChange={(e) => updateProduct(i, 'quantity', e.target.value)} placeholder="0" min="0" />
                </div>
              </div>
            </div>
          ))}

          {products.length < 3 && (
            <button onClick={addProduct} style={{ background: 'none', border: `1px dashed ${C.border}`, color: C.muted, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', width: '100%', marginBottom: 16, fontFamily: 'inherit' }}>
              + Add another product
            </button>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button onClick={() => setStep(3)} style={{ ...skipBtn, flex: 1, padding: 11 }}>
              Skip for now
            </button>
            <button onClick={saveStep2} disabled={saving} style={{ ...primaryBtn, flex: 2, padding: 11 }}>
              {saving ? 'Saving...' : 'Save & continue →'}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3 ── */}
      {step === 3 && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: C.text, marginBottom: 8 }}>Who is your first customer?</h2>
          <p style={{ color: C.muted, marginBottom: 24 }}>Add a customer to start tracking purchases</p>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: C.sub }}>Customer name</label>
            <input style={inputStyle} value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} placeholder="e.g. Kofi Mensah" />
          </div>
          <div style={{ marginBottom: 28 }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: C.sub }}>Email or phone</label>
            <input style={inputStyle} value={customer.contact} onChange={(e) => setCustomer((c) => ({ ...c, contact: e.target.value }))} placeholder="email@example.com or +233..." />
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => { setStep(4); }} style={{ ...skipBtn, flex: 1, padding: 11 }}>
              Skip for now
            </button>
            <button onClick={saveStep3} disabled={saving} style={{ ...primaryBtn, flex: 2, padding: 11 }}>
              {saving ? 'Saving...' : 'Save & continue →'}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 4 ── */}
      {step === 4 && (
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>🚀</div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: C.text, marginBottom: 12 }}>Your business is set up!</h2>

          {(summary.products.length > 0 || summary.customer) && (
            <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, margin: '20px 0', textAlign: 'left' }}>
              <p style={{ fontSize: '0.8rem', fontWeight: 600, color: C.muted, marginBottom: 10 }}>WHAT WE CREATED</p>
              {summary.products.map((p) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: '0.875rem', color: C.sub }}>
                  <span style={{ color: C.teal }}>✓</span> Product: {p.name}
                </div>
              ))}
              {summary.customer && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', color: C.sub }}>
                  <span style={{ color: C.teal }}>✓</span> Customer: {summary.customer.name}
                </div>
              )}
            </div>
          )}

          {/* TODO: Replace src with your YouTube/Loom walkthrough URL */}
          <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', borderRadius: 10, overflow: 'hidden', margin: '20px 0', background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}` }}>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill={C.ink}><path d="M8 5v14l11-7z"/></svg>
              </div>
              <p style={{ color: C.muted, fontSize: '0.85rem', margin: 0 }}>Walkthrough video coming soon</p>
            </div>
          </div>

          <p style={{ color: C.muted, marginBottom: 28, fontSize: '0.95rem' }}>
            Head to your dashboard to start recording sales and tracking your business performance.
          </p>
          <button onClick={finish} style={{ ...primaryBtn, width: '100%', padding: 14, fontSize: '1rem', boxShadow: '0 4px 16px rgba(240,180,41,0.3)' }}>
            Go to Dashboard →
          </button>
        </div>
      )}
    </div>
  );
}
